import { useEffect, useMemo, useState } from 'react'
import { BarChart3, ChevronDown, ChevronUp, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  DEFAULT_MAX_SELECTIONS,
  useFeatureSurveyStatus,
  useSubmitFeatureSurvey,
} from '../../hooks/shared/featureSurvey'
import { isFeatureSurveyEligible } from '../../hooks/shared/featureSurvey/types'
import { useToastStore } from '../../store/toastStore'

// Cookiebot Manager → Widget: 10px from right/bottom. Default badge is 48×48px.
const COOKIE_BOT_RIGHT_PX = 10
const COOKIE_BOT_BOTTOM_PX = 10
const COOKIE_BOT_BADGE_PX = 48
const COOKIE_BOT_GAP_PX = 12
const SURVEY_MINIMIZED_RIGHT_PX = COOKIE_BOT_RIGHT_PX + COOKIE_BOT_BADGE_PX + COOKIE_BOT_GAP_PX
const SURVEY_EXPANDED_RIGHT_PX = 20 // hug the viewport edge
const SURVEY_EXPANDED_BOTTOM_PX = 20

interface Props {
  isAuthenticated: boolean
}

export default function FeatureSurveyPopup({ isAuthenticated }: Props) {
  const { t } = useTranslation('featureSurvey')
  const addToast = useToastStore((s) => s.addToast)
  const [selected, setSelected] = useState<string[]>([])
  const [customByOption, setCustomByOption] = useState<Record<string, string>>({})
  const [customAnswer, setCustomAnswer] = useState('')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [submittedLocally, setSubmittedLocally] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)

  const { data, isLoading } = useFeatureSurveyStatus(isAuthenticated)
  const submitMutation = useSubmitFeatureSurvey()

  const minimizedStorageKey = data?.survey_key ? `feature-survey:${data.survey_key}:minimized` : null
  const dismissedStorageKey = data?.survey_key ? `feature-survey:${data.survey_key}:dismissed` : null

  useEffect(() => {
    if (!minimizedStorageKey) return
    try {
      setIsMinimized(localStorage.getItem(minimizedStorageKey) === 'true')
    } catch {
      setIsMinimized(false)
    }
  }, [minimizedStorageKey])

  useEffect(() => {
    if (!dismissedStorageKey) return
    try {
      setIsDismissed(localStorage.getItem(dismissedStorageKey) === 'true')
    } catch {
      setIsDismissed(false)
    }
  }, [dismissedStorageKey])

  useEffect(() => {
    if (!minimizedStorageKey) return
    try {
      localStorage.setItem(minimizedStorageKey, String(isMinimized))
    } catch {
      // Ignore storage errors; minimization is only a convenience.
    }
  }, [isMinimized, minimizedStorageKey])

  const maxSelections = data?.max_selections || DEFAULT_MAX_SELECTIONS
  const options = data?.options ?? []
  const customOptionIds = options.filter((option) => option.requires_custom).map((option) => option.id)
  const selectedCustomIds = customOptionIds.filter((id) => selected.includes(id))
  const hasCustomSelected = selectedCustomIds.length > 0
  const sliderOptions = useMemo(() => {
    if (maxSelections !== 1 || options.length < 3) return null
    const parsed = options
      .map((option) => {
        const numericValue = Number(option.label)
        if (!Number.isFinite(numericValue) || !Number.isInteger(numericValue)) return null
        return { option, value: numericValue }
      })
      .filter((row): row is { option: (typeof options)[number]; value: number } => row !== null)
      .sort((a, b) => a.value - b.value)
    if (parsed.length !== options.length) return null
    for (let i = 1; i < parsed.length; i += 1) {
      if (parsed[i].value !== parsed[i - 1].value + 1) return null
    }
    return parsed
  }, [maxSelections, options])
  const sliderMin = sliderOptions?.[0]?.value ?? 0
  const sliderMax = sliderOptions?.[sliderOptions.length - 1]?.value ?? 10
  const selectedSliderValue = useMemo(() => {
    if (!sliderOptions || selected.length === 0) return null
    const active = sliderOptions.find((row) => row.option.id === selected[0])
    return active?.value ?? null
  }, [selected, sliderOptions])

  const composedCustomAnswer = useMemo(() => {
    if (sliderOptions) return customAnswer.trim()
    const parts = selectedCustomIds
      .map((id) => {
        const option = options.find((row) => row.id === id)
        const value = (customByOption[id] || '').trim()
        if (!value) return null
        return selectedCustomIds.length > 1 ? `${option?.label ?? id}: ${value}` : value
      })
      .filter((part): part is string => Boolean(part))
    return parts.join('\n')
  }, [customAnswer, customByOption, options, selectedCustomIds, sliderOptions])

  const canSubmit = useMemo(() => {
    if (selected.length === 0 || selected.length > maxSelections) return false
    if (sliderOptions) return true
    if (hasCustomSelected) {
      return selectedCustomIds.every((id) => (customByOption[id] || '').trim().length >= 2)
    }
    return true
  }, [customByOption, hasCustomSelected, maxSelections, selected.length, selectedCustomIds, sliderOptions])

  if (
    !isAuthenticated
    || isLoading
    || !data?.is_active
    || !isFeatureSurveyEligible(data)
    || !data.survey_key
    || data.submitted
    || submittedLocally
    || isDismissed
  ) {
    return null
  }

  const handleDismiss = () => {
    if (!dismissedStorageKey) return
    try {
      localStorage.setItem(dismissedStorageKey, 'true')
    } catch {
      // Ignore storage errors.
    }
    setIsDismissed(true)
  }

  const toggleOption = (optionId: string) => {
    setSelected((current) => {
      if (current.includes(optionId)) {
        return current.filter((id) => id !== optionId)
      }
      if (current.length >= maxSelections) {
        addToast(t('popup.maxSelectionsToast', { max: maxSelections }), 'info')
        return current
      }
      return [...current, optionId]
    })
  }

  const toggleExpanded = (optionId: string) => {
    setExpanded((current) => ({ ...current, [optionId]: !current[optionId] }))
  }

  const handleSubmit = () => {
    submitMutation.mutate(
      {
        survey_key: data.survey_key,
        selected_options: selected,
        custom_answer: composedCustomAnswer || undefined,
      },
      {
        onSuccess: () => {
          setSubmittedLocally(true)
          addToast(t('popup.successToast'), 'success')
        },
        onError: (error: unknown) => {
          const detail =
            typeof error === 'object' &&
            error !== null &&
            'response' in error &&
            typeof (error as { response?: { data?: { detail?: string } } }).response?.data?.detail === 'string'
              ? (error as { response: { data: { detail: string } } }).response.data.detail
              : t('popup.errorToast')
          addToast(detail, 'error')
        },
      },
    )
  }

  return (
    <div
      className={`fixed z-50 ${
        isMinimized
          ? 'w-auto max-w-[min(28rem,calc(100vw-6rem))]'
          : 'w-[min(28rem,calc(100vw-2.5rem))]'
      }`}
      style={{
        right: isMinimized ? SURVEY_MINIMIZED_RIGHT_PX : SURVEY_EXPANDED_RIGHT_PX,
        bottom: isMinimized ? COOKIE_BOT_BOTTOM_PX : SURVEY_EXPANDED_BOTTOM_PX,
      }}
    >
      {isMinimized ? (
        <button
          type="button"
          onClick={() => setIsMinimized(false)}
          className="ml-auto flex items-center gap-2 rounded-full bg-[#212121] px-4 py-3 text-sm font-semibold text-white shadow-2xl hover:bg-[#333333]"
        >
          <BarChart3 className="h-4 w-4" />
          {t('popup.minimizedLabel', {
            title: data.title || t('popup.minimizedFallbackTitle'),
          })}
          <ChevronUp className="h-4 w-4" />
        </button>
      ) : (
        <section className="overflow-hidden rounded-2xl border border-orange-200 bg-white shadow-2xl">
          <div className="flex items-start justify-between gap-3 border-b border-orange-100 bg-orange-50 px-5 py-4">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-[#FF5A00]/10 p-2">
                <BarChart3 className="h-5 w-5 text-[#FF5A00]" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">{data.title}</h2>
                {data.description && (
                  <p className="mt-1 text-sm text-gray-600">{data.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setIsMinimized(true)}
                className="rounded-lg p-1.5 text-gray-500 hover:bg-white hover:text-gray-800"
                aria-label={t('popup.minimizeAria')}
              >
                <ChevronDown className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="rounded-lg p-1.5 text-gray-500 hover:bg-white hover:text-gray-800"
                aria-label={t('popup.closeAria')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[60vh] space-y-3 overflow-y-auto px-5 py-4">
            <div className="text-xs font-medium text-gray-500">
              {t('popup.selectedCount', { count: selected.length, max: maxSelections })}
            </div>
            {sliderOptions ? (
              <div className="space-y-3 rounded-xl border border-orange-100 bg-orange-50/40 p-4">
                <div className="text-center">
                  <div className="text-xs uppercase tracking-wide text-gray-500">{t('popup.npsScoreLabel')}</div>
                  <div className="mt-1 text-3xl font-bold text-[#FF5A00]">
                    {selectedSliderValue ?? sliderMin}
                  </div>
                </div>
                <input
                  type="range"
                  min={sliderMin}
                  max={sliderMax}
                  step={1}
                  value={selectedSliderValue ?? sliderMin}
                  onChange={(e) => {
                    const value = Number(e.target.value)
                    const match = sliderOptions.find((row) => row.value === value)
                    if (match) setSelected([match.option.id])
                  }}
                  className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-orange-200 accent-[#FF5A00]"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{sliderMin}</span>
                  <span>{sliderMax}</span>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {options.map((option) => {
                  const checked = selected.includes(option.id)
                  const hasDescription = Boolean(option.description?.trim())
                  const isExpanded = Boolean(expanded[option.id])
                  return (
                    <div
                      key={option.id}
                      className={`rounded-xl border px-3 py-2.5 text-sm transition ${
                        checked
                          ? 'border-[#FF5A00] bg-orange-50 text-gray-900'
                          : 'border-gray-200 bg-white text-gray-700'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-3">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOption(option.id)}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#FF5A00] focus:ring-[#FF5A00]"
                          />
                          <span className="font-medium leading-snug">{option.label}</span>
                        </label>
                        {hasDescription && (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(option.id)}
                            className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-[#FF5A00] hover:bg-orange-100"
                            aria-expanded={isExpanded}
                          >
                            {isExpanded ? t('popup.hideDetails') : t('popup.showDetails')}
                          </button>
                        )}
                      </div>
                      {hasDescription && isExpanded && (
                        <p className="mt-2 pl-7 text-xs leading-relaxed text-gray-600">
                          {option.description}
                        </p>
                      )}
                      {checked && option.requires_custom && (
                        <input
                          type="text"
                          value={customByOption[option.id] || ''}
                          onChange={(e) =>
                            setCustomByOption((current) => ({
                              ...current,
                              [option.id]: e.target.value,
                            }))
                          }
                          maxLength={500}
                          placeholder={
                            option.id.includes('marketplace')
                              ? t('popup.marketplacePlaceholder')
                              : t('popup.customPlaceholder')
                          }
                          className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#FF5A00] focus:outline-none focus:ring-2 focus:ring-[#FF5A00]/20"
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {sliderOptions && (
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  {t('popup.npsReasonLabel')}
                </label>
                <textarea
                  value={customAnswer}
                  onChange={(e) => setCustomAnswer(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder={t('popup.npsReasonPlaceholder')}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:border-[#FF5A00] focus:outline-none focus:ring-2 focus:ring-[#FF5A00]/20"
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-4">
            <button
              type="button"
              onClick={handleDismiss}
              className="text-sm font-medium text-gray-500 hover:text-gray-800"
            >
              {t('popup.skip')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || submitMutation.isPending}
              className="rounded-xl bg-[#FF5A00] px-4 py-2 text-sm font-semibold text-white hover:bg-[#e04e00] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitMutation.isPending ? t('popup.submitting') : t('popup.submit')}
            </button>
          </div>
        </section>
      )}
    </div>
  )
}
